// SPDX-License-Identifier: MIT
// Parse the output of `claude -p "<prompt>" --output-format json` (desktop
// bridge, step 2). The CLI wraps the model's answer in an envelope:
//   { "type":"result","subtype":"success","is_error":false,
//     "result":"<the model's text — here a fiche JSON string>", ... }
// We unwrap `.result`, then defensively extract + parse the fiche JSON (the CLI
// gives no Anthropic structured-output guarantee, so the model MIGHT wrap it in
// prose or a ```json fence — we tolerate that). Verified live on the VPS
// 2026-05-24: a magnify prompt yields a clean title/outline/first_steps/stack.
import type { MagnifyResult } from '../auth/magnify';

export class ClaudeBridgeError extends Error {}

// Pull the first balanced-looking JSON object out of a string (handles a
// ```json fence or surrounding prose around the object). Shared with the
// sovereign + converse parsers.
export function extractJsonObject(text: string): string {
  const fenced = text.match(/```(?:json)?\s*([\s\S]*?)```/i);
  const body = fenced ? fenced[1] : text;
  const start = body.indexOf('{');
  const end = body.lastIndexOf('}');
  if (start === -1 || end === -1 || end <= start) {
    throw new ClaudeBridgeError('no JSON object found in claude output');
  }
  return body.slice(start, end + 1);
}

// Unwrap the `claude -p --output-format json` envelope → the inner result text.
export function unwrapClaudeEnvelope(rawStdout: string): string {
  let env: Record<string, unknown>;
  try {
    env = JSON.parse(rawStdout) as Record<string, unknown>;
  } catch {
    throw new ClaudeBridgeError('claude output is not valid JSON (CLI envelope expected)');
  }
  if (env.is_error === true || (typeof env.subtype === 'string' && env.subtype !== 'success')) {
    throw new ClaudeBridgeError(`claude returned an error: ${String(env.subtype ?? env.result ?? 'unknown')}`);
  }
  const result = env.result;
  if (typeof result !== 'string' || result.trim().length === 0) {
    throw new ClaudeBridgeError('claude envelope has no usable result');
  }
  return result;
}

// Shared fiche extractor : a string that *contains* the fiche JSON (raw, fenced,
// or wrapped in prose) → a validated MagnifyResult. Reused by both bridges — the
// Claude CLI path (after envelope unwrap) and the sovereign-endpoint path (after
// the OpenAI message-content extraction). Throws ClaudeBridgeError on anything
// unusable so the caller can surface a clean "réessaye" / retry.
export function parseFicheJson(inner: string): MagnifyResult {
  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(extractJsonObject(inner)) as Record<string, unknown>;
  } catch (err) {
    throw err instanceof ClaudeBridgeError ? err : new ClaudeBridgeError('fiche JSON malformed');
  }
  if (
    typeof parsed.title !== 'string' ||
    !Array.isArray(parsed.outline) || parsed.outline.length === 0 ||
    !Array.isArray(parsed.first_steps) || parsed.first_steps.length === 0
  ) {
    throw new ClaudeBridgeError('fiche JSON missing required fields (title/outline/first_steps)');
  }
  return {
    title: parsed.title,
    outline: (parsed.outline as unknown[]).map(String),
    first_steps: (parsed.first_steps as unknown[]).map(String),
    ...(typeof parsed.stack === 'string' ? { stack: parsed.stack } : {}),
  };
}

// Full path: CLI stdout → MagnifyResult.
export function parseClaudeMagnify(rawStdout: string): MagnifyResult {
  return parseFicheJson(unwrapClaudeEnvelope(rawStdout));
}
